import React, { useMemo, useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Alert,
  AlertTitle,
  FormControlLabel,
  Switch
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { usePiers } from '../context/PiersContext';

const ConflictsView = () => {
  const { beltAssignments: assignments, configuration } = usePiers();
  const [expandedPairs, setExpandedPairs] = useState({});
  const [showExtendedConflicts, setShowExtendedConflicts] = useState(true);

  // Detect conflicts between belt pairs
  const conflicts = useMemo(() => {
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const getTimeDifference = (time1, time2) => {
      const mins1 = timeToMinutes(time1);
      const mins2 = timeToMinutes(time2);
      let diff = Math.abs(mins2 - mins1);
      if (diff > 720) {
        diff = 1440 - diff;
      }
      return diff;
    };

    const conflictList = [];
    const extendedWindow = 6 * 60; // 6 hours for extended conflicts

    // Check each belt pair
    configuration.beltPairs.forEach(([belt1, belt2]) => {
      if (belt1 > configuration.activeBelts || belt2 > configuration.activeBelts) return;

      const flights1 = assignments[belt1] || [];
      const flights2 = assignments[belt2] || [];

      // Check each combination of flights
      flights1.forEach(flight1 => {
        flights2.forEach(flight2 => {
          const timeDiff = getTimeDifference(flight1.depTime, flight2.depTime);
          
          // Check for shared destinations
          const sharedDestinations = flight1.destinations.filter(dest => 
            flight2.destinations.includes(dest)
          );

          if (sharedDestinations.length > 0) {
            // Determine conflict severity
            let severity = 'none';
            let type = '';
            
            if (timeDiff < configuration.conflictWindow * 60) {
              // Critical conflict (red) - within configured window
              severity = 'critical';
              type = 'Within Conflict Window';
            } else if (showExtendedConflicts && timeDiff < extendedWindow) {
              const windowMinutes = configuration.conflictWindow * 60;
              const midpoint = windowMinutes + (extendedWindow - windowMinutes) / 2;
              const fmt = (n) => Number.isInteger(n) ? `${n}` : n.toFixed(1);
              const windowHrs = configuration.conflictWindow;
              const midHrs = midpoint / 60;
              const extHrs = extendedWindow / 60;

              if (timeDiff < midpoint) {
                severity = 'warning';
                type = `Extended Conflict (${fmt(windowHrs)}-${fmt(midHrs)} hrs)`;
              } else {
                severity = 'caution';
                type = `Extended Conflict (${fmt(midHrs)}-${fmt(extHrs)} hrs)`;
              }
            }

            if (severity !== 'none') {
              conflictList.push({
                id: `${flight1.flightNum}-${flight2.flightNum}`,
                belt1,
                belt2,
                flight1,
                flight2,
                sharedDestinations,
                timeDifference: timeDiff,
                severity,
                type
              });
            }
          }
        });
      });
    });

    // Sort by severity and time difference
    const severityOrder = { critical: 0, warning: 1, caution: 2 };
    return conflictList.sort((a, b) => {
      const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
      if (severityDiff !== 0) return severityDiff;
      return a.timeDifference - b.timeDifference;
    });
  }, [assignments, configuration, showExtendedConflicts]);

  // Group conflicts by belt pair
  const conflictsByPair = useMemo(() => {
    const grouped = {};
    
    conflicts.forEach(conflict => {
      const pairKey = `${conflict.belt1}-${conflict.belt2}`;
      if (!grouped[pairKey]) {
        grouped[pairKey] = {
          belt1: conflict.belt1,
          belt2: conflict.belt2,
          conflicts: [],
          criticalCount: 0,
          warningCount: 0,
          cautionCount: 0
        };
      }
      grouped[pairKey].conflicts.push(conflict);
      grouped[pairKey][`${conflict.severity}Count`]++;
    });

    return Object.values(grouped);
  }, [conflicts]);

  // Get severity icon and color
  const getSeverityDisplay = (severity) => {
    switch (severity) {
      case 'critical':
        return { icon: <ErrorIcon />, color: '#FF5252', label: 'Conflict' };
      case 'warning':
        return { icon: <WarningIcon />, color: '#FF9800', label: 'Warning' };
      case 'caution':
        return { icon: <InfoIcon />, color: '#FFD600', label: 'Caution' };
      default:
        return { icon: null, color: '#666', label: 'Unknown' };
    }
  };

  const togglePairExpansion = (pairKey) => {
    setExpandedPairs(prev => ({
      ...prev,
      [pairKey]: !prev[pairKey]
    }));
  };

  const formatTimeDiff = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <Paper 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="h6" sx={{ color: '#C0C0C0' }}>
            Belt Pair Conflicts
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Destination conflicts within belt pairs
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch 
              checked={showExtendedConflicts}
              onChange={(e) => setShowExtendedConflicts(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#F9B612',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#F9B612',
                },
              }}
            />
          }
          label={`Show Extended Conflicts (${configuration.conflictWindow}-6 hrs)`}
          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
        />
      </Box>

      {/* Summary */}
      {conflicts.length > 0 && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(0, 0, 0, 0.2)' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Total Conflicts: {conflicts.length}
            </Typography>
            {conflicts.filter(c => c.severity === 'critical').length > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${conflicts.filter(c => c.severity === 'critical').length} Conflicts`}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255, 82, 82, 0.2)',
                  color: '#FF5252',
                  '& .MuiChip-icon': { color: '#FF5252' }
                }}
              />
            )}
            {conflicts.filter(c => c.severity === 'warning').length > 0 && (
              <Chip
                icon={<WarningIcon />}
                label={`${conflicts.filter(c => c.severity === 'warning').length} Warnings`}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255, 152, 0, 0.2)',
                  color: '#FF9800',
                  '& .MuiChip-icon': { color: '#FF9800' }
                }}
              />
            )}
            {conflicts.filter(c => c.severity === 'caution').length > 0 && (
              <Chip
                icon={<InfoIcon />}
                label={`${conflicts.filter(c => c.severity === 'caution').length} Cautions`}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255, 214, 0, 0.2)',
                  color: '#FFD600',
                  '& .MuiChip-icon': { color: '#FFD600' }
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Conflicts Table */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {conflictsByPair.length === 0 ? (
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Alert 
              severity="success" 
              sx={{ 
                bgcolor: 'rgba(76, 175, 80, 0.1)',
                color: '#4CAF50',
                '& .MuiAlert-icon': { color: '#4CAF50' }
              }}
            >
              <AlertTitle>No Conflicts Detected</AlertTitle>
              All belt pair assignments are conflict-free!
            </Alert>
          </Box>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'rgba(0, 0, 0, 0.3)', color: '#C0C0C0' }}>
                    Belt Pair
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(0, 0, 0, 0.3)', color: '#C0C0C0' }}>
                    Conflicts
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(0, 0, 0, 0.3)', color: '#C0C0C0' }}>
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conflictsByPair.map((pairData) => {
                  const pairKey = `${pairData.belt1}-${pairData.belt2}`;
                  const isExpanded = expandedPairs[pairKey];

                  return (
                    <React.Fragment key={pairKey}>
                      <TableRow 
                        hover
                        sx={{ 
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                          cursor: 'pointer'
                        }}
                        onClick={() => togglePairExpansion(pairKey)}
                      >
                        <TableCell sx={{ color: '#C0C0C0' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" sx={{ color: '#F9B612' }}>
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <Typography variant="body2" fontWeight={600}>
                              {configuration.beltNames?.[pairData.belt1] || `Belt ${pairData.belt1}`} - {configuration.beltNames?.[pairData.belt2] || `Belt ${pairData.belt2}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {pairData.criticalCount > 0 && (
                              <Chip
                                size="small"
                                label={pairData.criticalCount}
                                sx={{ bgcolor: '#FF5252', color: '#FFF' }}
                              />
                            )}
                            {pairData.warningCount > 0 && (
                              <Chip
                                size="small"
                                label={pairData.warningCount}
                                sx={{ bgcolor: '#FF9800', color: '#FFF' }}
                              />
                            )}
                            {pairData.cautionCount > 0 && (
                              <Chip
                                size="small"
                                label={pairData.cautionCount}
                                sx={{ bgcolor: '#FFD600', color: '#000' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {pairData.conflicts.length} total conflicts
                        </TableCell>
                      </TableRow>

                      {/* Expanded conflict details */}
                      <TableRow>
                        <TableCell colSpan={3} sx={{ p: 0 }}>
                          <Collapse in={isExpanded}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.2)' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ color: '#999' }}>Severity</TableCell>
                                    <TableCell sx={{ color: '#999' }}>Flight 1</TableCell>
                                    <TableCell sx={{ color: '#999' }}>Flight 2</TableCell>
                                    <TableCell sx={{ color: '#999' }}>Destinations</TableCell>
                                    <TableCell sx={{ color: '#999' }}>Time Diff</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {pairData.conflicts.map((conflict) => {
                                    const severityDisplay = getSeverityDisplay(conflict.severity);
                                    return (
                                      <TableRow key={conflict.id}>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ color: severityDisplay.color }}>
                                              {severityDisplay.icon}
                                            </Box>
                                            <Typography 
                                              variant="caption" 
                                              sx={{ color: severityDisplay.color }}
                                            >
                                              {severityDisplay.label}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#C0C0C0' }}>
                                          <Typography variant="body2">
                                            {conflict.flight1.flightNum}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                            {conflict.flight1.depTime} ({configuration.beltNames?.[conflict.belt1] || `Belt ${conflict.belt1}`})
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: '#C0C0C0' }}>
                                          <Typography variant="body2">
                                            {conflict.flight2.flightNum}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                            {conflict.flight2.depTime} ({configuration.beltNames?.[conflict.belt2] || `Belt ${conflict.belt2}`})
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: '#C0C0C0' }}>
                                          {conflict.sharedDestinations.join(', ')}
                                        </TableCell>
                                        <TableCell sx={{ color: severityDisplay.color }}>
                                          {formatTimeDiff(conflict.timeDifference)}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
};

export default ConflictsView;